/**
 * Deep content extension for ECOSYSTEM_TOPICS.
 *
 * Phase 6.3 — Deep Category Content Architecture.
 *
 * Adds editorial body, quotes, curated reads, and product recommendations
 * to every ecosystem hub.  Kept in a separate file to keep the base
 * `ecosystemTopics.js` config easy to scan.
 *
 * Merge strategy (applied inside ecosystemTopics.js):
 *   merged = { ...base, ...DEEP_CONTENT[key] }
 * so any field provided here is added/overridden on top of the base.
 */

export const DEEP_CONTENT = {
  // ============================================================
  // WELLNESS
  // ============================================================
  "wellness/mental": {
    editorial: [
      {
        title: "Stress isn't the enemy — chronic stress is",
        body:
          "Your body is built to handle acute stress. The threat in modern life isn't the surge; it's the failure to come down. Mental wellness is the skill of returning to baseline — and the daily structures that make that return automatic.\n\nStart small: a 4-minute breath reset after the workday, walking meetings, no phones in bed. Each act is a vote for a quieter nervous system.",
      },
      {
        title: "The four pillars of burnout recovery",
        body:
          "Rest helps, but it's not enough. Sustainable recovery rests on four legs: meaningful work, autonomy, deep recovery, and human connection. Audit which one is missing — and rebuild from there.\n\nDonny can help you map a personal recovery plan inside this corner of the ecosystem.",
      },
    ],
    quotes: [
      { text: "Between stimulus and response there is a space. In that space is our power to choose our response.", author: "Viktor Frankl" },
      { text: "Almost everything will work again if you unplug it for a few minutes — including you.", author: "Anne Lamott" },
      { text: "Calm is a superpower. Pace yourself like it.", author: "DONiTSH!T Editorial" },
    ],
    reads: [
      { tag: "Editorial", title: "Burnout vs. depression: how to tell the difference", summary: "Subtle but important distinctions for anyone wondering which one they're in.", to: "/ask-donny" },
      { tag: "Practice", title: "A 4-minute breath reset for the workday", summary: "Box breathing, eye softening, and a single deliberate exhale. Try it now.", to: "/ask-donny" },
      { tag: "Reflection", title: "What's one boundary you've avoided for too long?", summary: "Open Donny and explore — gently.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Program", title: "Calm Mind, 21 Days", summary: "A 21-day daily program of micro-practices for stress, anxiety, and resilience.", to: "/shop" },
      { kind: "eBook", title: "The Burnout Recovery Map", summary: "An honest, structural guide to coming back to yourself.", to: "/shop" },
      { kind: "Course", title: "Confidence as a Skill", summary: "Multi-week course to rebuild self-trust from the inside.", to: "/shop" },
    ],
  },

  "wellness/nutrition": {
    editorial: [
      {
        title: "Eat for your dosha — not against your body",
        body:
          "Ayurveda's gift to modern nutrition isn't a rulebook; it's the idea that what fuels one person can inflame another. Vata thrives on warm, grounding foods. Pitta on cooling and bitter. Kapha on light, dry, and stimulating.\n\nStart with one meal a day adapted to your constitution. Watch your energy curve change in a week.",
      },
      {
        title: "Hydration is a strategy, not a chore",
        body:
          "Drink before you're thirsty, with electrolytes if you train hard, and warm in the morning to wake your digestive fire (agni). The biggest hydration mistake isn't volume — it's timing.",
      },
    ],
    quotes: [
      { text: "Let food be thy medicine, and medicine be thy food.", author: "Hippocrates" },
      { text: "You don't have to eat less. You have to eat right.", author: "Deepak Chopra" },
      { text: "Agni first. Always.", author: "Ayurvedic principle" },
    ],
    reads: [
      { tag: "Guide", title: "What a balanced plate looks like — dosha edition", summary: "Practical visual examples, no calorie counting.", to: "/ask-donny" },
      { tag: "Recipe", title: "Citrus-ginger morning tonic", summary: "A 60-second drink that wakes digestion and brain.", to: "/ask-donny" },
      { tag: "Habit", title: "Eat slow. Notice. Stop at 80%.", summary: "The simplest framework for healthy eating.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Program", title: "30-Day Dosha Reset", summary: "A month of dosha-aligned eating with simple, beautiful recipes.", to: "/shop" },
      { kind: "eBook", title: "The Real Plate", summary: "A field guide for eating in a chaotic world.", to: "/shop" },
      { kind: "Bundle", title: "Adaptogen Tea Trio", summary: "Three teas for morning energy, midday focus, and evening calm.", to: "/shop" },
    ],
  },

  "wellness/sleep": {
    editorial: [
      {
        title: "Sleep is built — not earned",
        body:
          "You don't get great sleep by deserving it. You build it. Anchor your wake time. Get bright light early. Move daily. Stop caffeine after noon. Dim lights two hours before bed.\n\nDo that for two weeks and you'll need fewer hours to feel more rested.",
      },
      {
        title: "The wind-down ritual that actually works",
        body:
          "Dim, warm, slow. The brain reads cues from the environment. Lower the lights. Lower the temperature in your bedroom. Lower the pace. A 10-minute ritual — stretch, herbal tea, journaling, no phone — is more effective than any supplement.",
      },
    ],
    quotes: [
      { text: "Sleep is the single most effective thing we can do to reset our brain and body health.", author: "Matthew Walker" },
      { text: "Tired is not a personality trait.", author: "DONiTSH!T Editorial" },
      { text: "Night-time is the body's repair shift. Don't interrupt it.", author: "Wellness aphorism" },
    ],
    reads: [
      { tag: "Routine", title: "The 90-minute sleep cycle, explained", summary: "Why your wake-up time matters more than you think.", to: "/ask-donny" },
      { tag: "Practice", title: "Build your wind-down in 5 minutes", summary: "A guided template you can personalize tonight.", to: "/ask-donny" },
      { tag: "Reflection", title: "What's one thing keeping you up?", summary: "Open Donny and find a strategy.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Program", title: "Deep Sleep 14", summary: "Two weeks to a quieter, deeper night.", to: "/shop" },
      { kind: "eBook", title: "The Sleep Architecture Guide", summary: "REM, deep, light — and why fragmentation matters.", to: "/shop" },
      { kind: "Bundle", title: "Evening Calm Kit", summary: "Tea, journal prompts, and a soundscape.", to: "/shop" },
    ],
  },

  "wellness/sports": {
    editorial: [
      {
        title: "Recovery is not the opposite of training — it's part of it",
        body:
          "The athletes who last train hard, then recover harder. Sleep, nutrition, mobility, mental decompression. If you only optimize the training load, you eventually break. If you optimize the whole loop, you compound for years.",
      },
      {
        title: "An honest word on PEDs",
        body:
          "Steroid awareness isn't endorsement — it's harm reduction. Conversations are happening among amateur athletes whether the wellness world is ready or not. Education on risks, signs of misuse, and how to support a friend is part of a complete sports-performance program.",
      },
    ],
    quotes: [
      { text: "Discipline is the bridge between goals and accomplishment.", author: "Jim Rohn" },
      { text: "Train smarter, not just harder.", author: "DONiTSH!T Editorial" },
      { text: "Rest is a competitive advantage.", author: "Performance science" },
    ],
    reads: [
      { tag: "Training", title: "Periodization for working athletes", summary: "Build a season around your life, not the other way around.", to: "/ask-donny" },
      { tag: "Recovery", title: "Mobility before training — and after", summary: "The 8-minute mobility flow that prevents 80% of soft-tissue niggles.", to: "/ask-donny" },
      { tag: "Awareness", title: "PED risks: a calm, honest primer", summary: "What every serious amateur should know.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Program", title: "Athlete Reset 21", summary: "Three weeks of mobility, recovery, and nervous-system care.", to: "/shop" },
      { kind: "eBook", title: "The Real Recovery Stack", summary: "What works, what's marketing, and what's worth your time.", to: "/shop" },
      { kind: "Course", title: "Mental Performance Foundations", summary: "Focus, pre-competition rituals, and consistency under pressure.", to: "/shop" },
    ],
  },

  // ============================================================
  // RELATIONSHIPS
  // ============================================================
  "relationships/luv-connect": {
    editorial: [
      {
        title: "Date your standards, not your loneliness",
        body:
          "Luv Connect is built on a simple principle: clarity beats hope. Knowing your values, season, and non-negotiables protects your nervous system more than any algorithm.\n\nThe people who find lasting partnership tend to do their own work first — and date intentionally second.",
      },
      {
        title: "Compatibility beyond chemistry",
        body:
          "Chemistry is real. So is the question of whether you'd actually want to live with someone. Look at lifestyle pace, conflict styles, financial values, and how they treat people who can't help them. Those tell the truth.",
      },
    ],
    quotes: [
      { text: "The most important relationship you'll ever have is the one with yourself.", author: "Diane von Furstenberg" },
      { text: "Love is friendship that has caught fire.", author: "Ann Landers" },
      { text: "Slow down to find the right one — speed is the enemy of intuition.", author: "DONiTSH!T Editorial" },
    ],
    reads: [
      { tag: "Reflection", title: "A 10-question values check before you date", summary: "Save yourself months. Answer these first.", to: "/ask-donny" },
      { tag: "Practice", title: "Designing your first three dates", summary: "Structure for connection without pressure.", to: "/ask-donny" },
      { tag: "Story", title: "What a healthy beginning looks like", summary: "A composite story from the DONiTSH!T community.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Program", title: "Self-Worth Foundations", summary: "Six-week program to date from a grounded, confident place.", to: "/shop" },
      { kind: "eBook", title: "Modern Dating, Intentionally", summary: "A field guide for the slow-romantic.", to: "/shop" },
      { kind: "Course", title: "Attachment in Action", summary: "Understand your style and rewrite the script.", to: "/shop" },
    ],
  },

  "relationships/couples-connect": {
    editorial: [
      {
        title: "Weekly check-ins beat annual blow-ups",
        body:
          "The healthiest couples we see have one quiet hour per week. Phones away. Three questions: How was your week, in one sentence? What did I miss? What do we need to plan?\n\nThat hour saves marriages.",
      },
      {
        title: "Repair, the only skill that matters",
        body:
          "All couples fight. The healthy ones repair faster. A good repair is specific (\"I'm sorry I snapped about the dishes\"), takes responsibility, and includes a plan (\"Next time I'll say I need a 10-minute reset\"). That's it. That's the secret.",
      },
    ],
    quotes: [
      { text: "We are not enemies, but friends. We must not be enemies.", author: "Abraham Lincoln" },
      { text: "Choose each other on purpose — every single day.", author: "DONiTSH!T Editorial" },
      { text: "Intimacy = into-me-see.", author: "Couples-therapy aphorism" },
    ],
    reads: [
      { tag: "Ritual", title: "The weekly couples check-in template", summary: "Three questions. One hour. Massive impact.", to: "/ask-donny" },
      { tag: "Conflict", title: "The 5 minutes after a fight matter most", summary: "How to repair before the wound deepens.", to: "/ask-donny" },
      { tag: "Intimacy", title: "Desire in long-term relationships", summary: "Why it ebbs — and the conditions in which it returns.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Program", title: "The 8-Week Reconnect", summary: "Guided couples program — small daily acts, weekly check-ins.", to: "/shop" },
      { kind: "eBook", title: "Conflict to Repair", summary: "The repair toolkit for partners who want to last.", to: "/shop" },
      { kind: "Course", title: "Intimacy After Years", summary: "Reconnect, deepen, and stay curious.", to: "/shop" },
    ],
  },

  "relationships/healthy": {
    editorial: [
      {
        title: "Healthy relationships are quiet",
        body:
          "Drama is not the same as passion. Healthy relationships feel calm, supportive, and energizing — most of the time. There's still difficulty, still growth, still hard conversations. But the baseline is safety, not chaos.\n\nIf the only highs you've known are mountains and valleys, healthy can feel boring at first. Stay with it. Boring is peace in disguise.",
      },
      {
        title: "Repair is the single highest-leverage skill",
        body:
          "Long-term relationship research agrees: it's not the absence of rupture that matters, it's the presence of repair. Practice apologizing without defensiveness. Practice receiving an apology with grace. The rest follows.",
      },
    ],
    quotes: [
      { text: "Love is not gazing at each other but looking together in the same direction.", author: "Antoine de Saint-Exupéry" },
      { text: "Strong individuals make stronger couples.", author: "DONiTSH!T Editorial" },
      { text: "Boundaries are an act of love.", author: "Brené Brown" },
    ],
    reads: [
      { tag: "Markers", title: "10 signs of a securely-attached relationship", summary: "What healthy actually looks like, day to day.", to: "/ask-donny" },
      { tag: "Practice", title: "Apologizing without defensiveness", summary: "A drill you can practice solo.", to: "/ask-donny" },
      { tag: "Reflection", title: "Are you in a relationship or a pattern?", summary: "A gentle audit prompt.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Course", title: "Secure-Functioning Relationships", summary: "Build the skills of secure attachment as an adult.", to: "/shop" },
      { kind: "eBook", title: "The Repair Toolkit", summary: "Concrete scripts for hard conversations.", to: "/shop" },
      { kind: "Program", title: "Boundaries Without Walls", summary: "Hold your ground while staying open.", to: "/shop" },
    ],
  },

  "relationships/communication": {
    editorial: [
      {
        title: "Most arguments are listening failures",
        body:
          "We think we're arguing about dishes. We're arguing about feeling unseen. Slow your response. Reflect what you heard. Ask, \"Is there more?\" Watch the conversation change.",
      },
      {
        title: "A formula for difficult conversations",
        body:
          "Try this: \"When [observation], I felt [feeling], because I need [need]. Would you be willing to [request]?\" It feels mechanical at first. It also works.",
      },
    ],
    quotes: [
      { text: "The single biggest problem in communication is the illusion that it has taken place.", author: "George Bernard Shaw" },
      { text: "Seek first to understand, then to be understood.", author: "Stephen Covey" },
      { text: "Tone is a sentence's hidden meaning.", author: "DONiTSH!T Editorial" },
    ],
    reads: [
      { tag: "Skill", title: "Reflective listening in 60 seconds", summary: "A drill that changes how you hear.", to: "/ask-donny" },
      { tag: "Tool", title: "The NVC observation/feeling/need/request loop", summary: "A clean structure for any hard conversation.", to: "/ask-donny" },
      { tag: "Script", title: "Starting a difficult talk with someone you love", summary: "First-sentence templates that lower the temperature.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Course", title: "Conversations That Connect", summary: "A four-week course on listening, framing, and repair.", to: "/shop" },
      { kind: "eBook", title: "Saying Hard Things With Care", summary: "Scripts and reframes for the conversations you've been avoiding.", to: "/shop" },
      { kind: "Program", title: "30 Days of Better Talking", summary: "One micro-practice a day.", to: "/shop" },
    ],
  },

  "relationships/dating": {
    editorial: [
      {
        title: "Date intentionally — your time matters",
        body:
          "Modern dating rewards stamina, not strategy. Pick a small number of qualities that genuinely matter to you. Filter on those, not on optionality. The point isn't to maximize matches; it's to spend your evenings with people you'd want to know in five years.",
      },
      {
        title: "Green flags are quieter than red",
        body:
          "Someone who's calm in stress, kind to staff, curious about your inner life, and clear about their values is a much bigger green flag than chemistry on a first date. Train your eye to see the slow signals.",
      },
    ],
    quotes: [
      { text: "Don't settle for someone who fits your loneliness — choose someone who fits your wholeness.", author: "DONiTSH!T Editorial" },
      { text: "If they wanted to, they would. If they don't, they won't.", author: "Common wisdom" },
      { text: "Your standards are not too high — your tolerance has been too high.", author: "Anon." },
    ],
    reads: [
      { tag: "Profile", title: "Writing a profile that filters in the right people", summary: "Less polish, more truth.", to: "/ask-donny" },
      { tag: "Conversation", title: "The first-three-dates conversation map", summary: "What to talk about, when.", to: "/ask-donny" },
      { tag: "Energy", title: "Dating without burning out", summary: "Pacing, recovery, and protecting your peace.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Program", title: "Intentional Dating, 30 Days", summary: "A month of small daily practices that change the way you date.", to: "/shop" },
      { kind: "eBook", title: "Profile, Pacing, Presence", summary: "A working guide to modern dating.", to: "/shop" },
      { kind: "Course", title: "Attachment in Dating", summary: "Recognize your patterns. Rewrite them.", to: "/shop" },
    ],
  },

  "relationships/wellness": {
    editorial: [
      {
        title: "Friendship is healthcare",
        body:
          "Adult friendships are the wellness pillar nobody talks about. They require maintenance — text first, schedule first, show up first. The dividend is enormous: better immunity, lower stress hormones, longer life.",
      },
      {
        title: "The relationship with yourself comes first",
        body:
          "How you talk to yourself sets the tone of every other relationship. Notice the voice. Soften it. The work is small, daily, and life-changing.",
      },
    ],
    quotes: [
      { text: "The greatest predictor of long-term wellbeing isn't diet or exercise — it's the quality of your relationships.", author: "Harvard Study of Adult Development" },
      { text: "Loneliness is a form of malnutrition.", author: "DONiTSH!T Editorial" },
      { text: "Be your own first responder.", author: "Self-compassion principle" },
    ],
    reads: [
      { tag: "Friendship", title: "Texting first is a wellness skill", summary: "Why connection-initiation matters in adulthood.", to: "/ask-donny" },
      { tag: "Self", title: "An audit of your inner voice", summary: "What would you say to a friend? Say it to yourself.", to: "/ask-donny" },
      { tag: "Family", title: "Patterns we inherit, patterns we choose", summary: "On family dynamics and growth.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Program", title: "Connection Reset", summary: "Three weeks to re-energize your friendships and family ties.", to: "/shop" },
      { kind: "eBook", title: "Self-Compassion as a Skill", summary: "Practical drills, not platitudes.", to: "/shop" },
      { kind: "Course", title: "The Art of Adult Friendship", summary: "Maintain, deepen, and choose your circle.", to: "/shop" },
    ],
  },

  // ============================================================
  // FAMILIES & PARENTING
  // ============================================================
  "families/parenting": {
    editorial: [
      {
        title: "Connection first, correction second",
        body:
          "A child who feels seen will listen. A child who feels managed will resist. Before discipline, make contact. Get down to their level. Name the feeling. Then guide the behavior.\n\nThis is harder than it sounds — and the single most important skill of modern parenting.",
      },
      {
        title: "You can't pour from an empty cup",
        body:
          "Parent self-care isn't a luxury — it's parenting infrastructure. Sleep, friendship, a hobby that's just yours, ten minutes of nothing. These aren't selfish. They're how you become the parent your child needs.",
      },
    ],
    quotes: [
      { text: "Children are not things to be molded, but people to be unfolded.", author: "Jess Lair" },
      { text: "Make connection the goal. Behavior follows.", author: "DONiTSH!T Editorial" },
      { text: "Calm parents raise calm kids.", author: "Developmental psychology" },
    ],
    reads: [
      { tag: "Practice", title: "The 5-minute repair after a parenting fail", summary: "We all snap. Repair quickly. Model how it's done.", to: "/ask-donny" },
      { tag: "Tool", title: "Time-in, not time-out", summary: "Why proximity beats isolation for most kids.", to: "/ask-donny" },
      { tag: "Self", title: "A weekly parent reset, in 20 minutes", summary: "A small ritual that protects your patience.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Program", title: "The Calm Parent, 30 Days", summary: "Tiny daily practices that change the tone of your home.", to: "/shop" },
      { kind: "eBook", title: "Discipline Without Shame", summary: "Setting limits with warmth.", to: "/shop" },
      { kind: "Course", title: "Attachment-Aware Parenting", summary: "Apply attachment science to daily life.", to: "/shop" },
    ],
  },

  "families/wellness": {
    editorial: [
      {
        title: "Family wellness is built on rhythm, not heroics",
        body:
          "Forget the wellness retreat. The real practice is the dinner table on Tuesday. The Sunday morning walk. The Saturday phone-down hour. Small rhythms outperform grand efforts, every time.",
      },
      {
        title: "Tech in healthy homes",
        body:
          "Screens aren't the enemy — context is. Phones charge outside bedrooms. No screens at meals. One family screen night, by choice, not default. Healthy homes have agreements, not bans.",
      },
    ],
    quotes: [
      { text: "The family is the first school of human love.", author: "John Paul II" },
      { text: "Rituals are the architecture of family memory.", author: "DONiTSH!T Editorial" },
      { text: "A family eats together becomes one.", author: "Cross-cultural wisdom" },
    ],
    reads: [
      { tag: "Ritual", title: "A simple weekly family rhythm", summary: "The Sunday plan that makes the week feel handled.", to: "/ask-donny" },
      { tag: "Meals", title: "Why dinner is your highest-leverage habit", summary: "What 20 minutes a day actually does.", to: "/ask-donny" },
      { tag: "Tech", title: "Designing a screen-healthy household", summary: "Practical agreements that hold up.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Program", title: "Family Wellness, 8 Weeks", summary: "Guided rhythms for meals, sleep, movement, and connection.", to: "/shop" },
      { kind: "eBook", title: "Rituals That Last", summary: "Build the small practices that hold a family together.", to: "/shop" },
      { kind: "Course", title: "Tech-Healthy Homes", summary: "Make peace with screens, on your terms.", to: "/shop" },
    ],
  },

  // ============================================================
  // BUSINESS & CORPORATE
  // ============================================================
  "business/partners": {
    editorial: [
      {
        title: "Quality over quantity",
        body:
          "We'd rather have 100 great partners than 10,000 average listings. Every business in our directory has been reviewed for credentials, ethics, and the quality of care they actually deliver.",
      },
      {
        title: "Why listing here matters",
        body:
          "Visibility across the entire DONiTSH!T ecosystem — homepage, hub pages, Donny, Dosha results, Marketplace. Premium listings get featured placement. Standard listings are always free.",
      },
    ],
    quotes: [
      { text: "Wellness is the new luxury — and care is the new currency.", author: "DONiTSH!T Editorial" },
      { text: "We don't grow alone. We grow in relationship.", author: "Community principle" },
    ],
    reads: [
      { tag: "Guide", title: "What makes a business 'DONiTSH!T-aligned'", summary: "Our partner principles, in plain language.", to: "/ask-donny" },
      { tag: "Setup", title: "Building a partner profile that converts", summary: "Photos, copy, and the offer that closes.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Service", title: "Featured Partner Placement", summary: "Premium visibility across the ecosystem.", to: "/marketplace/register" },
      { kind: "Service", title: "Standard Listing (Free)", summary: "A free baseline listing for any vetted wellness pro.", to: "/marketplace/register" },
    ],
  },

  "business/corporate": {
    editorial: [
      {
        title: "Wellness that actually moves the needle",
        body:
          "Most corporate wellness fails because it's a meditation app pasted on top of a culture that doesn't sleep. Real impact requires touching the work — autonomy, recovery, leadership wellness, and meaningful connection between teammates.",
      },
      {
        title: "Leaders go first",
        body:
          "If leadership doesn't take wellness seriously, no program will land. We start with the people who set the temperature: the leadership team. Then we cascade.",
      },
    ],
    quotes: [
      { text: "Burned-out leaders create burned-out teams.", author: "DONiTSH!T Editorial" },
      { text: "Culture eats wellness program for breakfast.", author: "Variation on Peter Drucker" },
    ],
    reads: [
      { tag: "Framework", title: "A wellness audit for any company in 60 minutes", summary: "Honest, structural, useful.", to: "/ask-donny" },
      { tag: "Program", title: "Designing a wellness program for a 50-person team", summary: "From workshops to 1:1 coaching.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Service", title: "Wellness Audit", summary: "An honest assessment of your org's wellness posture.", to: "/support" },
      { kind: "Service", title: "Leadership Coaching", summary: "Premium 1:1 coaching for execs and founders.", to: "/support" },
      { kind: "Service", title: "Team Workshops", summary: "On-site and virtual workshops on stress, communication, and resilience.", to: "/support" },
    ],
  },

  "business/offers": {
    editorial: [
      {
        title: "Public preview. Member codes.",
        body:
          "Every offer is visible to everyone. The promo code itself unlocks once you sign in — our way of keeping member benefits truly for members.",
      },
      {
        title: "How offers are chosen",
        body:
          "We rotate a small, curated set of offers each month from partner businesses across categories. Quality first, always.",
      },
    ],
    quotes: [
      { text: "A great offer is generous and specific.", author: "DONiTSH!T Editorial" },
    ],
    reads: [
      { tag: "How", title: "Reveal a member-only code", summary: "Sign up free — your codes are waiting.", to: "/signup" },
      { tag: "Categories", title: "Browse offers by category", summary: "Therapy, retreats, coaching, fitness, corporate.", to: "/marketplace" },
    ],
    products: [
      { kind: "Membership", title: "DONiTSH!T Member (Free)", summary: "Unlock all member-only promo codes by signing up.", to: "/signup" },
    ],
  },

  "business/directory": {
    editorial: [
      {
        title: "Browse intentionally",
        body:
          "Use the directory as a starting point. Open the full Marketplace for filters by category, location, dosha alignment, and ratings.",
      },
      {
        title: "Looking for the right kind of help?",
        body:
          "Therapists treat symptoms. Coaches build skills. Nutritionists shape food. Studios shape movement. Donny can help you pick — open the chat and describe what you're going through.",
      },
    ],
    quotes: [
      { text: "The right pro at the right moment can shift years.", author: "DONiTSH!T Editorial" },
    ],
    reads: [
      { tag: "Guide", title: "Therapist vs. Coach: how to choose", summary: "Two different roles, two different moments.", to: "/ask-donny" },
      { tag: "Tool", title: "What to ask in a discovery call", summary: "Five questions that save weeks.", to: "/ask-donny" },
    ],
    products: [
      { kind: "Service", title: "List Your Business (Free)", summary: "A free baseline listing for any vetted wellness pro.", to: "/marketplace/register" },
    ],
  },
};
